import { format, formatDistanceToNow } from 'date-fns';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    Enums,
    errorRedirect,
    HelpTooltip,
    IDisposable,
    inject,
    Loader,
    Modal,
    ModalHandle,
    softwareVersionToString,
    TasksStore,
    Tooltip,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsArrowClockwise } from '@react-icons/all-files/bs/BsArrowClockwise';
import { BsChevronDoubleUp } from '@react-icons/all-files/bs/BsChevronDoubleUp';
import { LinkContainer } from 'react-router-bootstrap';
import { Link } from 'react-router-dom';
import {
    Button,
    ButtonGroup,
    Collapse,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledButtonDropdown,
} from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { AppStore } from '../Core/AppStore';
import { DisplaysStore } from '../Displays/DisplaysStore';
import { ManifestsStore } from './ManifestsStore';
import { LinkedManifestModal } from './LinkedManifestModal';
import { BsExclamationTriangle } from '@react-icons/all-files/bs/BsExclamationTriangle';
import { ActiveDisplay } from '../Displays/ActiveDisplay';
import {BsSlashCircle} from "@react-icons/all-files/bs/BsSlashCircle";

export interface IManifestDisplayItemProps {
    readonly display: ActiveDisplay;
    readonly softwareUpdateManifestGuid: string;
}

@autoinject
@observer
export class ManifestDisplayItem extends React.Component<IManifestDisplayItemProps> {
    @bind
    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            await this.displaysStore.loadSoftwareUpdateStatusAsync(this.props.display.serialNumber);
            this.handle = await this.displaysStore.connectionManager.connect();
            await this.displaysStore.subscribeToDisplayAsync(this.props.display.serialNumber);
        } catch (e: any) {
            errorRedirect(e);
        } finally {
            this.loading = false;
        }
    }

    public async componentWillUnmount() {
        try {
            await this.displaysStore.unsubscribeToDisplayAsync(this.props.display.serialNumber);
            this.handle?.dispose();
        } catch (e: any) {
            console.warn(e);
        }
    }

    public render() {
        const { display } = this.props;
        return (
            <div className="card p-2">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-xs-12 col-md-1 d-flex">
                            <div className="d-md-none pe-2">Display:</div>
                            <div className="d-md-none">
                                <LinkContainer to={`/displays/${display.serialNumber}`}>
                                    <Button className="p-0" color="link">
                                        {display.serialNumber}
                                    </Button>
                                </LinkContainer>
                            </div>
                            <div className="d-none d-md-inline-block">
                                <LinkContainer to={`/displays/${display.serialNumber}`}>
                                    <Button color="link">{display.serialNumber}</Button>
                                </LinkContainer>
                            </div>

                            {this.displayUpdateStatus?.linkedSoftwareUpdateManifests &&
                                this.displayUpdateStatus?.linkedSoftwareUpdateManifests.length > 1 && (
                                <Tooltip message="Linked Manifests">
                                    <Button type="submit" color="danger" onClick={this.openLinkedManifestsModal}>
                                        <BsExclamationTriangle />
                                    </Button>
                                </Tooltip>
                            )}

                            <LinkedManifestModal
                                displaySerial={display.serialNumber}
                                softwareUpdateManifestGuid={this.props.softwareUpdateManifestGuid}
                                modalHandle={this.linkedManifestsModal}
                            />
                        </div>

                        <div className="col-xs-12 col-md-2 d-flex">
                            <div className="d-md-none pe-2">Display Status:</div>
                            <HelpTooltip
                                subjects={['Downtime Incidents', 'Display Statuses']}
                                name={display.statusTooltip}
                                underline
                                placement="top"
                            >
                                <div className="d-inline-block display-row-events-small">
                                    <div>{this.displayStatusMessage[0]}</div>
                                </div>
                            </HelpTooltip>
                        </div>

                        <div className="col-xs-12 col-md-1 d-flex">
                            <div className="d-md-none pe-2">Last Poll:</div>

                            <span>{this.displayPollTime}</span>
                        </div>

                        <div className="col-xs-12 col-md-2 d-flex">
                            <div className="d-md-none pe-2">Error:</div>
                            <span>{this.status?.exceptionMessage ?? '-'}</span>
                        </div>

                        <div className="col-xs-12 col-md-1 d-flex">
                            <div className="d-md-none pe-2">Files:</div>
                            <div className="d-md-none pe-2">
                                <button onClick={this.onToggle} className="btn btn-link p-0">
                                    {this.fileSystemUpdates.length ?? '-'}
                                </button>
                            </div>
                            <div className="d-none d-md-inline-block pe-2">
                                <button onClick={this.onToggle} className="btn btn-link">
                                    {this.fileSystemUpdates.length ?? '-'}
                                </button>
                            </div>
                        </div>

                        <div className="col-xs-12 col-md-1 d-flex">
                            <div className="d-md-none pe-2">Mri:</div>
                            <div className="d-md-none pe-2">
                                {softwareVersionToString(display.displaySoftware?.mriSoftwareVersion)}
                            </div>
                            <div className="d-none d-md-inline-block pe-2">
                                {softwareVersionToString(display.displaySoftware?.mriSoftwareVersion)}
                            </div>
                        </div>

                        <div className="col-xs-12 col-md-2 d-flex">
                            <span>Status: {Enums.toString(Enums.SoftwareUpdateState, this.isUpdatedState)}</span>
                            {this.updating && (
                                <Tooltip message="Update in progress">
                                    <BsChevronDoubleUp className=" text-running text-pulse ps-2" />
                                </Tooltip>
                            )}
                            {this.showClear && (
                                <Tooltip message="Clear Status">
                                    <div className="pe-2">
                                        <button onClick={this.clearUpdateStatus} className="btn btn-link ms-2 p-0">
                                            <BsSlashCircle style={{ fontSize: 18 }} className="text-danger" />
                                        </button>
                                    </div>
                                  
                                </Tooltip>
                            )}
                        </div>

                        <div className="col-xs-12 col-md-2 d-flex">
                            <div className="ms-md-auto">
                                <ButtonGroup size="sm">
                                    {!this.updated && !this.updating && (
                                        <Tooltip message="Update Now" delay={0}>
                                            <Button color="primary" onClick={this.updateNow}>
                                                <BsChevronDoubleUp className="text-running" />
                                            </Button>
                                        </Tooltip>
                                    )}
                                    
                                    {this.updating && (
                                        <Tooltip message="Cancel Update" delay={0}>
                                            <Button color="danger" onClick={this.cancelUpdate}>
                                                <BsSlashCircle style={{ fontSize: 18 }} className="text-white" />
                                            </Button>
                                        </Tooltip>
                                    )}

                                    <UncontrolledButtonDropdown>
                                        <DropdownToggle caret color="primary" size="sm">
                                            Upload Status
                                        </DropdownToggle>
                                        <DropdownMenu onClick={this.toggleDropdown}>
                                            <DropdownItem
                                                tag="a"
                                                href={`${this.accountStore.portalUrl}/proxy/${display.serialNumber}/cgi-bin/uploadStatus`}
                                                target="_blank"
                                                className="small"
                                            >
                                                StatusVu&reg;
                                            </DropdownItem>

                                            <DropdownItem
                                                tag={Link}
                                                className="small"
                                                to={`/displays/${display.serialNumber}`}
                                            >
                                                SmartVu&reg;
                                            </DropdownItem>

                                            <DropdownItem
                                                tag={Button}
                                                className="small"
                                                onClick={this.clearObjectException}
                                            >
                                                <small>Clear Object Reference Error</small>
                                            </DropdownItem>
                                        </DropdownMenu>
                                    </UncontrolledButtonDropdown>
                                </ButtonGroup>
                            </div>
                        </div>
                    </div>
                </div>

                {this.status && !this.collapsed && (
                    <Collapse isOpen={!this.collapsed}>
                        <div className="container-fluid ms-4">
                            <div className="row">
                                <div className="col-2 border-bottom">File</div>
                                <div className="col-2 border-bottom">Target Filesystem</div>
                                <div className="col-4 border-bottom">Status</div>
                            </div>
                            {this.fileSystemUpdates.map(
                                f =>
                                    f.targetFileSystem && (
                                        <div className="row" key={f.targetFileSystem.fileSystemGuid}>
                                            <div className="col-2 small">{f.targetFileSystem.fileName || '-'}</div>

                                            <div className="col-2 small">
                                                {this.fullRevisionString(f.targetFileSystem) || '-'}
                                            </div>

                                            <div className="col-4 small">
                                                {f.exceptionMessage
                                                    ? this.getErrorMessage(f.exceptionMessage)
                                                    : f.status ?? '-'}
                                            </div>
                                        </div>
                                    )
                            )}
                        </div>
                    </Collapse>
                )}

                <Modal
                    className="modal-sm"
                    title="Confirm Deletion"
                    modalHandle={this.modalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">
                        The "Null Object Reference" error is an issue with orleans breaking down the JSON of the update
                        status. Are you sure the error is a "Null Object Reference" error?
                    </div>
                </Modal>
            </div>
        );
    }

    @bind
    public getErrorMessage(error: any) {
        return typeof error == 'string'
            ? error
            : typeof error == 'object' && typeof error.message == 'string'
                ? error.message
                : typeof error == 'object' && typeof error.Message == 'string'
                    ? error.Message
                    : 'An error has occured. Please try again.';
    }

    @bind
    @action
    private onToggle() {
        this.collapsed = !this.collapsed;
    }

    @bind
    @action
    private toggleDropdown() {
        this.dropdownOpen = !this.dropdownOpen;
    }

    @bind
    @action
    private async clearObjectException() {
        if (await this.modalHandle.confirm()) {
            const task = this.tasksStore.startSessionTask(
                'Clear ' + this.props.display.serialNumber + ' Update Exception'
            );
            try {
                await this.displaysStore.clearSoftwareUpdateStatusException(this.props.display.serialNumber);
            } catch (e: any) {
                task.error(e.message);
            } finally {
                task.complete();
            }
        }
    }

    @bind
    private async clearUpdateStatus() {
        const task = this.tasksStore.startSessionTask(`Clear ${this.props.display.serialNumber} status`);

        try {
            await this.displaysStore.clearSoftwareUpdateStatusAsync(this.props.display.serialNumber);

            task.complete();
        } catch (e: any) {
            task.error(e);
        }
    }

    @bind
    private async updateNow(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        const { softwareUpdateManifestGuid, display } = this.props;

        const task = this.tasksStore.startSessionTask('Start Update');
        try {
            task.complete();
            await this.manifestsStore.updateDisplayNowAsync(softwareUpdateManifestGuid, display.serialNumber);
        } catch (e: any) {
            console.error(e);
            task.error(e);
        }
    }
    
    @bind
    private async cancelUpdate(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        const { softwareUpdateManifestGuid, display } = this.props;

        const task = this.tasksStore.startSessionTask('Cancel Update');
        try {
            await this.manifestsStore.cancelDisplayUpdateAsync(display.serialNumber);
            task.complete();
        } catch (e: any) {
            console.error(e);
            task.error(e);
        }
    }
    
    @computed
    private get showClear() {
        return this.status != null;
    }

    @computed
    private get updating() {
        return (
            this.status?.state == Mri.DisplaySoftware.SoftwareUpdateState.Pending ||
            this.status?.state == Mri.DisplaySoftware.SoftwareUpdateState.Updating ||
            this.status?.state == Mri.DisplaySoftware.SoftwareUpdateState.UpdatingSlave
        );
    }

    @computed
    private get updated() {
        return (
            this.status?.state == Mri.DisplaySoftware.SoftwareUpdateState.Updated &&
            this.status?.softwareUpdateManifestGuid == this.props.softwareUpdateManifestGuid
        );
    }

    @computed
    private get idle() {
        return (
            this.status?.state == Mri.DisplaySoftware.SoftwareUpdateState.Idle ||
            (this.status?.state == Mri.DisplaySoftware.SoftwareUpdateState.Updated &&
                this.status?.softwareUpdateManifestGuid != this.props.softwareUpdateManifestGuid)
        );
    }

    @computed
    private get isUpdatedState() {
        return this.status == null || this.idle // Catch idle explicitly for updated check
            ? Mri.DisplaySoftware.SoftwareUpdateState.Idle
            : this.status.state;
    }

    @bind
    private fullRevisionString(fileSystem: Mri.FileSystems.FileSystem) {
        const fsType = Enums.toString(Enums.FileSystemType, fileSystem.softwareVersion.fsType);
        const productType = this.productType(fileSystem.softwareVersion.fsType, fileSystem.softwareVersion.productType);
        const majorRevision = fileSystem.softwareVersion.majorRevision;
        const minorRevision = fileSystem.softwareVersion.minorRevision;
        const buildType = Enums.toString(Enums.FileSystemBuildType, fileSystem.softwareVersion.buildType);

        return `${fsType}: ${productType}-${majorRevision}.${minorRevision} - ${buildType}`;
    }

    @bind
    private productType(fsType: number, productType: number) {
        const productTypeNames = Enums.FileSystemProductTypes.get(fsType);
        if (productTypeNames) {
            const name = productTypeNames.get(productType);
            if (name) {
                return `${name} - ${productType}`;
            } else {
                return productType;
            }
        } else {
            return productType;
        }
    }

    @computed
    private get status() {
        const displayStatus = this.displaysStore.updateStatuses.get(this.props.display.serialNumber);
        return displayStatus;
    }

    @computed
    private get displayPollTime() {
        const pollTime = this.props.display.pollTime;
        const message = pollTime
            ? pollTime < new Date()
                ? formatDistanceToNow(pollTime, { addSuffix: true })
                : '< 1 min'
            : '-';

        return message;
    }

    @bind
    private openLinkedManifestsModal() {
        this.linkedManifestsModal.open();
    }

    @computed
    private get fileSystemUpdates() {
        if (this.status && this.status.pendingFileSystemUpdates) return this.status?.pendingFileSystemUpdates;
        return [];
    }

    @computed
    private get displayUpdateStatus() {
        return this.displaysStore.updateStatuses.get(this.props.display.serialNumber);
    }

    @computed
    private get displayStatusMessage() {
        return this.props.display.statusMessage.split('\n').map((s, idx) => s);
    }

    private linkedManifestsModal = new ModalHandle();
    @observable
    private dropdownOpen = false;
    @observable
    private collapsed = true;
    @observable
    private modalHandle = new ModalHandle();
    @observable
    private loading = false;
    private handle?: IDisposable;
    @inject
    private readonly accountStore!: AccountStore;
    @inject
    private readonly displaysStore!: DisplaysStore;
    @inject
    private readonly tasksStore!: TasksStore;
    @inject
    private manifestsStore!: ManifestsStore;
    @inject
    private appStore!: AppStore;
}
